import Finance from 'financejs';
import CONFIG from './config';

// Constants
const INFLATION_RATE = 0.07;
const POST_RETIREMENT_DISCOUNT_RATE = 0.094;
const LIFE_EXPECTANCY = 85;
const CURRENT_YEAR = new Date().getFullYear();

// Risk profile to discount rate mapping
const RISK_PROFILES = {
  'Conservative': POST_RETIREMENT_DISCOUNT_RATE,
  'Balanced': 0.107,
  'Aggressive': 0.12
};

/**
 * Calculate present value using the financial formula
 */
export const presentValue = (rate, nper, pmt, fv, type = 1) => {
  if (rate === 0) {
    return -((pmt * nper) + fv);
  } else {
    return -(pmt * ((1 - Math.pow(1 + rate, -nper)) / rate) + fv / Math.pow(1 + rate, nper));
  }
};

/**
 * Calculate age-related values
 */
export const calculateAgeValues = (formData) => {
  const results = {};
  
  if (formData.birthYear > 0) {
    results.age = CURRENT_YEAR - formData.birthYear;
    
    // Set default retirement age if not set
    if (formData.retirementAge === 0) {
      results.retirementAge = results.age;
    }
    
    // Calculate years to retirement
    if (formData.retirementAge > 0) {
      results.yearsToRetirement = formData.retirementAge - results.age;
    }
  }
  
  return results;
};

/**
 * Calculate retirement corpus and related values
 */
export const calculateRetirementValues = (formData) => {
  const results = {};
  
  if (formData.yearlyExpenses > 0 && formData.yearsToRetirement > 0) {
    const monthlyInflationRate = Math.pow(1 + INFLATION_RATE, 1 / 12) - 1;
    const monthlyPostRetirementDiscountRate = Math.pow(1 + POST_RETIREMENT_DISCOUNT_RATE, 1 / 12) - 1;
    
    const currentRetirementExpenses = formData.yearlyExpenses * 12;
    const yearsToRetirement = formData.yearsToRetirement;
    
    results.yearsInRetirement = LIFE_EXPECTANCY - formData.retirementAge;
    
    // Calculate Future Value of current retirement expenses
    results.FVCurrentRetirementExpenses = currentRetirementExpenses * 
      Math.pow(1 + INFLATION_RATE, yearsToRetirement);
    
    // Calculate the corpus required at retirement
    results.corpusRequired = (results.FVCurrentRetirementExpenses / 12) *
      (1 - Math.pow((1 + monthlyInflationRate) / (1 + monthlyPostRetirementDiscountRate), 
        results.yearsInRetirement * 12)) /
      (monthlyPostRetirementDiscountRate - monthlyInflationRate);
  }
  
  return results;
};

/**
 * Corpus required at retirement, based on the current risk profile.
 * Returns null when there isn't enough input to compute it, so callers can skip
 * writing a bogus value into context.
 * Depends on riskProfile — must be re-run whenever the profile changes.
 */
export const calculateCorpusRequired = (state) => {
  if (!state.monthlyExpensesInRetirement || state.monthlyExpensesInRetirement <= 0) return null;

  const monthlyInflationRate              = Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, 1 / 12) - 1;
  const postRetirementReturn              = CONFIG.DEFAULT_POST_RETIREMENT_RETURN[state.riskProfile]
                                            ?? CONFIG.DEFAULT_POST_RETIREMENT_RETURN['Conservative'];
  const monthlyPostRetirementDiscountRate = Math.pow(1 + postRetirementReturn, 1 / 12) - 1;
  const yearlyExpensesInRetirement        = state.monthlyExpensesInRetirement * 12;
  const yearsInRetirement                 = CONFIG.DEFAULT_LIFE_EXPECTANCY - state.retirementAge;
  const retirementExpenses_FV             = yearlyExpensesInRetirement
                                            * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, state.yearsForRetirement);
  const corpusRequired                    = (retirementExpenses_FV / 12)
                                            * (1 - Math.pow(
                                                (1 + monthlyInflationRate) / (1 + monthlyPostRetirementDiscountRate),
                                                yearsInRetirement * 12
                                              ))
                                            / (monthlyPostRetirementDiscountRate - monthlyInflationRate);

  return Number.isFinite(corpusRequired) ? Math.round(corpusRequired) : null;
};

/**
 * Calculate goal-related present values
 */
export const calculateGoalValues = (formData) => {
  const results = {
    shortTermGoalsSumPV: 0,
    longTermGoalsSumPV: 0,
    shortTermGoalsSumFV: 0,
    longTermGoalsSumFV: 0
  };
  
  if (!formData.goals || formData.goals.length === 0) {
    return results;
  }
  
  const discountRate = RISK_PROFILES[formData.riskProfile] || POST_RETIREMENT_DISCOUNT_RATE;
  const debtReturn = formData.debt_return || 0.0610;
  
  formData.goals.forEach(goal => {
    const yearsToGoal = Math.max(0, goal.targetYear - CURRENT_YEAR - 3);
    
    let shortTermGoal = 3;
    if (goal.targetYear - CURRENT_YEAR < 3) {
      shortTermGoal = goal.targetYear - CURRENT_YEAR;
    }
    
    const intermediatePV = presentValue(discountRate, yearsToGoal, 0, Number(goal.FV_goal), 0);
    const presentValueGoals = presentValue(debtReturn, shortTermGoal, 0, intermediatePV, 1);
    
    // Categorize based on time horizon
    if (goal.targetYear - CURRENT_YEAR <= 3) {
      results.shortTermGoalsSumPV += presentValueGoals;
      results.shortTermGoalsSumFV += Number(goal.FV_goal);
    } else {
      results.longTermGoalsSumPV += presentValueGoals;
      results.longTermGoalsSumFV += Number(goal.FV_goal);
    }
  });
  
  return results;
};

/**
 * Calculate required amount today
 */
export const calculateAmountRequiredToday = (formData, goalValues) => {
  if (!formData.corpusRequired || !formData.yearsToRetirement) {
    return 0;
  }
  
  const discountRate = RISK_PROFILES[formData.riskProfile] || POST_RETIREMENT_DISCOUNT_RATE;
  const totalPresentValueGoals = goalValues.shortTermGoalsSumPV + goalValues.longTermGoalsSumPV;
  
  return (formData.corpusRequired / Math.pow(1 + discountRate, formData.yearsToRetirement)) + 
         totalPresentValueGoals;
};

/**
 * Calculate funding score and required rate of return
 */
export const calculateFundingValues = (formData) => {
  const results = {
    fundingScore: 0,
    requiredRateOfReturn: 0
  };
  
  if (formData.currentInvestment <= 0 || formData.amountRequiredToday <= 0) {
    return results;
  }
  
  // Calculate funding score
  const fundingRatio = formData.currentInvestment / formData.amountRequiredToday;
  results.fundingScore = Math.min(100, Math.round(fundingRatio * 100));
  
  // Calculate required rate of return using XIRR
  if (formData.yearsToRetirement > 0) {
    try {
      const finance = new Finance();
      const monthlyInflationRate = Math.pow(1 + INFLATION_RATE, 1 / 12) - 1;
      const retirementYear = CURRENT_YEAR + formData.yearsToRetirement;
      
      // Prepare retirement cash flows
      const datesRet = [];
      const amountTillRet = [];
      let monthlyExp = formData.FVCurrentRetirementExpenses / 12;
      
      for (let year = 0; year < formData.yearsInRetirement; year++) {
        for (let month = 1; month < 13; month++) {
          const monthAct = month < 10 ? '0' + month : month;
          datesRet.push(`${retirementYear + year}-${monthAct}-01`);
          amountTillRet.push(monthlyExp * -1);
          monthlyExp *= (1 + monthlyInflationRate);
        }
      }
      
      // Prepare goal cash flows
      const goalsDates = formData.goals.map(goal => `${goal.targetYear}-01-01`);
      const goalsValues = formData.goals.map(goal => -1 * Number(goal.FV_goal));
      
      // Combine all cash flows
      const cashFlows = [formData.currentInvestment];
      const dates = [`${CURRENT_YEAR}-01-01`];
      
      dates.push(...datesRet, ...goalsDates);
      cashFlows.push(...amountTillRet, ...goalsValues);
      
      const dateObjects = dates.map(d => new Date(d));
      
      // Check if we have both positive and negative cash flows
      const hasPositive = cashFlows.some(value => value > 0);
      const hasNegative = cashFlows.some(value => value < 0);
      
      if (hasPositive && hasNegative) {
        results.requiredRateOfReturn = finance.XIRR(cashFlows, dateObjects);
      }
    } catch (error) {
      console.error('Error calculating XIRR:', error);
      results.requiredRateOfReturn = 0;
    }
  }
  
  return results;
};

/**
 * Main function to calculate all derived values
 */
export const calculateAllValues = (formData) => {
  let results = { ...formData };
  
  // Calculate age-related values
  const ageValues = calculateAgeValues(results);
  results = { ...results, ...ageValues };
  
  // Calculate retirement values
  const retirementValues = calculateRetirementValues(results);
  results = { ...results, ...retirementValues };
  
  // Set discount rate based on risk profile
  if (results.riskProfile) {
    results.discount_rate = RISK_PROFILES[results.riskProfile] || POST_RETIREMENT_DISCOUNT_RATE;
  }
  
  // Calculate goal values
  const goalValues = calculateGoalValues(results);
  results = { ...results, ...goalValues };
  
  // Calculate amount required today
  results.amountRequiredToday = calculateAmountRequiredToday(results, goalValues);
  
  // Calculate funding values
  const fundingValues = calculateFundingValues(results);
  results = { ...results, ...fundingValues };
  
  return results;
};

/**
 * Validate form data for each step
 */
export const validateFormData = (formData) => {
  const currentYear = new Date().getFullYear();
  
  return {
    0: formData.birthYear > 1900 && formData.birthYear <= currentYear, // Income (using birth year as proxy)
    1: formData.yearlyExpenses > 0, // Expense
    2: true, // Debt (can be 0)
    3: true, // Mutual Funds (can be 0)
    4: true, // Equity (can be 0)
    5: true, // Others (can be 0)
  };
};

// Export constants for use in components
export const CONSTANTS = {
  INFLATION_RATE,
  POST_RETIREMENT_DISCOUNT_RATE,
  LIFE_EXPECTANCY,
  CURRENT_YEAR,
  RISK_PROFILES
};
